import React from 'react';
import { getGlobalLogger } from '@zg-rentals/logger-base';

const logger = getGlobalLogger('contexts/ModalContext/ModalErrorBoundary');

export interface ModalErrorBoundaryProps {
  children: React.ReactNode;
  modalType: string;
}

interface ModalErrorBoundaryState {
  hasError: boolean;
  error?: Error;
}

/**
 * Error boundary specifically designed for modal components.
 * Catches JavaScript errors anywhere in the child component tree during rendering,
 * in lifecycle methods, and in constructors of the whole tree below them.
 *
 * Note: Error boundaries must be class components in React. They cannot be
 * functional components because they need to implement componentDidCatch
 * and getDerivedStateFromError lifecycle methods.
 */
class ModalErrorBoundary extends React.Component<ModalErrorBoundaryProps, ModalErrorBoundaryState> {
  constructor(props: ModalErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): ModalErrorBoundaryState {
    // Update state so the next render will show the fallback UI
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Log the error for debugging
    logger?.error(`Modal "${this.props.modalType}" encountered an error:`, error, errorInfo);
  }

  // Reset error boundary when modal type changes
  componentDidUpdate(prevProps: ModalErrorBoundaryProps) {
    if (prevProps.modalType !== this.props.modalType) {
      this.setState({ hasError: false, error: undefined });
    }
  }

  private handleRetry = () => {
    this.setState({ hasError: false, error: undefined });
  };

  render() {
    if (this.state.hasError) {
      return (
        <div role="alert" style={{ padding: '20px', textAlign: 'center' }}>
          <h2>Modal Error</h2>
          <p>Something went wrong with the {this.props.modalType} modal.</p>
          <button
            onClick={this.handleRetry}
            style={{
              marginTop: '10px',
              padding: '5px 10px',
              backgroundColor: '#007cba',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
            }}
          >
            Try Again
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ModalErrorBoundary;
